import { useReducer, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import {
  ReferenceCard,
  PromptCard,
  ImagesGrid,
  ImageViewModal,
  SaveAvatarModal,
} from '@/components/ai-avatar';
import type { Id } from '../../convex/_generated/dataModel';
import {
  type IGeneratedImage,
  type IReferencePersonOption,
  type TAspectRatio,
} from '@/@types/avatars';
import { useAvatarGeneration } from '@/hooks/useAvatarGeneration';

interface IAvatarGenState {
  selectedPersonId: Id<'referencePersons'> | null;
  selectedRatio: TAspectRatio;
  imageCount: 1 | 2 | 3 | 4;
  prompt: string;
  attachedImage: IGeneratedImage | null;
  viewingImage: IGeneratedImage | null;
}

type AvatarGenAction =
  | { type: 'SET_PERSON'; id: Id<'referencePersons'> | null }
  | { type: 'SET_RATIO'; ratio: TAspectRatio }
  | { type: 'SET_COUNT'; count: 1 | 2 | 3 | 4 }
  | { type: 'SET_PROMPT'; prompt: string }
  | { type: 'SET_ATTACHED'; image: IGeneratedImage | null }
  | { type: 'SET_VIEWING'; image: IGeneratedImage | null };

function avatarGenReducer(state: IAvatarGenState, action: AvatarGenAction): IAvatarGenState {
  switch (action.type) {
    case 'SET_PERSON':
      return { ...state, selectedPersonId: action.id };
    case 'SET_RATIO':
      return { ...state, selectedRatio: action.ratio };
    case 'SET_COUNT':
      return { ...state, imageCount: action.count };
    case 'SET_PROMPT':
      return { ...state, prompt: action.prompt };
    case 'SET_ATTACHED':
      return { ...state, attachedImage: action.image };
    case 'SET_VIEWING':
      return { ...state, viewingImage: action.image };
  }
}

const INITIAL_AVATAR_GEN: IAvatarGenState = {
  selectedPersonId: null,
  selectedRatio: '9:16',
  imageCount: 1,
  prompt: '',
  attachedImage: null,
  viewingImage: null,
};

export const AvatarGeneratePage = () => {
  const navigate = useNavigate();

  const referencePersons = useQuery(api.referencePersons.getActiveReferencePersons);
  const folders = useQuery(api.avatarFolders.getActiveFolders);

  const [state, dispatch] = useReducer(avatarGenReducer, INITIAL_AVATAR_GEN);

  useEffect(() => {
    if (referencePersons?.length && !state.selectedPersonId) {
      dispatch({ type: 'SET_PERSON', id: referencePersons[0]._id });
    }
  }, [referencePersons, state.selectedPersonId]);

  const referencePersonOptions = useMemo<IReferencePersonOption[]>(() => {
    if (!referencePersons) return [];
    return referencePersons.map(
      (person: { _id: Id<'referencePersons'>; name: string; imageUrls?: string[] }) => ({
        id: person._id,
        name: person.name,
        imageUrl: person.imageUrls?.[0],
      })
    );
  }, [referencePersons]);

  const selectedPerson = referencePersons?.find(
    (p: { _id: Id<'referencePersons'> }) => p._id === state.selectedPersonId
  );

  const setAttachedImage = (image: IGeneratedImage | null) =>
    dispatch({ type: 'SET_ATTACHED', image });
  const setPrompt = (prompt: string) => dispatch({ type: 'SET_PROMPT', prompt });

  const gen = useAvatarGeneration({
    selectedPersonId: state.selectedPersonId,
    prompt: state.prompt,
    selectedRatio: state.selectedRatio,
    imageCount: state.imageCount,
    attachedImage: state.attachedImage,
    setAttachedImage,
    setPrompt,
  });

  const isLoading = referencePersons === undefined;

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background overflow-auto">
        <div className="p-8 max-w-7xl mx-auto h-full flex flex-col">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/ai-avatar/my-avatars')}
              className="h-10 w-10 border border-border"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Generate Avatar</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Create AI-generated images with character consistency
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          ) : referencePersons.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center">
              <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground">No reference persons</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Create a reference person first to generate consistent avatar images.
              </p>
              <Button
                onClick={() => navigate('/ai-avatar/reference-persons')}
                className="mt-4"
                size="sm"
              >
                Add Reference Person
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-6 flex-1 min-h-0">
              <div className="flex gap-6 items-end">
                <ReferenceCard
                  selectedPersonId={state.selectedPersonId}
                  onPersonChange={id => dispatch({ type: 'SET_PERSON', id })}
                  referencePersons={referencePersonOptions}
                  isLoading={false}
                />
                <PromptCard
                  prompt={state.prompt}
                  onPromptChange={setPrompt}
                  selectedRatio={state.selectedRatio}
                  onRatioChange={ratio => dispatch({ type: 'SET_RATIO', ratio })}
                  imageCount={state.imageCount}
                  onImageCountChange={count => dispatch({ type: 'SET_COUNT', count })}
                  attachedImage={state.attachedImage}
                  onRemoveAttachment={() => setAttachedImage(null)}
                  onImprove={gen.handleImprove}
                  onGenerate={gen.handleGenerate}
                  isGenerating={gen.isGenerating}
                  isImproving={gen.isImproving}
                  disabled={!state.selectedPersonId}
                />
              </div>

              {gen.error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {gen.error}
                </div>
              )}

              <ImagesGrid
                images={gen.generatedImages}
                selectedIds={gen.selectedImageIds}
                onToggleSelect={gen.handleToggleSelect}
                onSelectAll={gen.handleSelectAll}
                onDeselectAll={gen.handleDeselectAll}
                onView={image => dispatch({ type: 'SET_VIEWING', image })}
                onDownload={gen.handleDownload}
                onEdit={gen.handleEdit}
                onDelete={gen.handleDelete}
                onBulkDelete={gen.handleBulkDelete}
                onBulkDownload={gen.handleBulkDownload}
                onSave={gen.handleSave}
                isGenerating={gen.isGenerating}
              />
            </div>
          )}

          <ImageViewModal
            image={state.viewingImage}
            isOpen={state.viewingImage !== null}
            onClose={() => dispatch({ type: 'SET_VIEWING', image: null })}
            onDownload={() => state.viewingImage && gen.handleDownload(state.viewingImage)}
            onEdit={() => state.viewingImage && gen.handleEdit(state.viewingImage)}
            onDelete={() => state.viewingImage && gen.handleDelete(state.viewingImage.storageId)}
          />

          <SaveAvatarModal
            isOpen={gen.isSaveDialogOpen}
            onClose={() => gen.setIsSaveDialogOpen(false)}
            onSave={gen.handleSaveConfirm}
            isSaving={gen.isSaving}
            selectedImages={gen.selectedImages}
            folders={folders || []}
            selectedFolderId={gen.selectedFolderId}
            onFolderChange={gen.setSelectedFolderId}
            newFolderTitle={gen.newFolderTitle}
            onNewFolderTitleChange={gen.setNewFolderTitle}
            isDefault={gen.isDefault}
            onDefaultChange={gen.setIsDefault}
            referencePersonName={selectedPerson?.name || ''}
          />
        </div>
      </div>
    ),
  };
};
